import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';

function AddMeal() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Meal form state
  const [mealForm, setMealForm] = useState({
    mealName: '',
    mealType: '', // Break, Lunch, Snack
    frequency: '', // Daily, Weekly, Monthly
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  // Meal form functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMealForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };


  const validateForm = () => {
    const errors = {};
    
    if (!mealForm.mealName.trim()) {
      errors.mealName = 'Meal name is required';
    }
    
    if (!mealForm.mealType.trim()) {
      errors.mealType = 'Meal type is required';
    }
    
    if (!mealForm.frequency.trim()) {
      errors.frequency = 'Frequency is required';
    }
    
    if (!mealForm.description.trim()) {
      errors.description = 'Description is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create meal data
      const mealData = {
        school_id: schoolData.id,
        meal_name: mealForm.mealName.trim(),
        meal_type: mealForm.mealType,
        frequency: mealForm.frequency,
        description: mealForm.description.trim(),
        created_at: new Date().toISOString()
      };
      
      console.log('Saving meal data to Supabase:', mealData);
      
      const { data, error } = await supabase
        .from('meals')
        .insert(mealData)
        .select()
        .single();
      
      if (error) {
        console.error('Error saving meal:', error);
        alert(`Failed to add meal: ${error.message}`);
        setIsSubmitting(false);
        return;
      }
      
      console.log('Meal added successfully:', data);
      
      // Reset form
      setMealForm({
        mealName: '',
        mealType: '',
        frequency: '',
        description: ''
      });
      setFormErrors({});
      
      // Show success popup
      setShowSuccessPopup(true);
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to add meal. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">⏳</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1>Add Meal</h1>
          <div className="user-info">
            <span>{schoolData?.school_name || 'School'}</span>
            <div className="user-avatar">🏫</div>
          </div>
        </div>
        
        <div className="content-body">
          <div className="dashboard-content">
            <div className="add-teacher-section">
              <div className="add-teacher-card">
                <form onSubmit={handleSubmit} className="add-teacher-form">
                  <div className="form-section">
                    <h3>Meal Information</h3>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="mealName">Meal Name *</label>
                        <input
                          type="text"
                          id="mealName"
                          name="mealName"
                          value={mealForm.mealName}
                          onChange={handleInputChange}
                          className={formErrors.mealName ? 'error' : ''}
                          placeholder="e.g., Chicken Biryani, Vegetable Sandwich"
                        />
                        {formErrors.mealName && (
                          <span className="error-text">{formErrors.mealName}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="mealType">Meal Type *</label>
                        <select
                          id="mealType"
                          name="mealType"
                          value={mealForm.mealType}
                          onChange={handleInputChange}
                          className={formErrors.mealType ? 'error' : ''}
                        >
                          <option value="">Select meal type</option>
                          <option value="Break">Break</option>
                          <option value="Lunch">Lunch</option>
                          <option value="Snack">Snack</option>
                        </select>
                        {formErrors.mealType && (
                          <span className="error-text">{formErrors.mealType}</span>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="frequency">Frequency *</label>
                        <select
                          id="frequency"
                          name="frequency"
                          value={mealForm.frequency}
                          onChange={handleInputChange}
                          className={formErrors.frequency ? 'error' : ''}
                        >
                          <option value="">Select frequency</option>
                          <option value="Daily">Daily</option>
                          <option value="Weekly">Weekly</option>
                          <option value="Monthly">Monthly</option>
                        </select>
                        {formErrors.frequency && (
                          <span className="error-text">{formErrors.frequency}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="description">Description *</label>
                        <textarea
                          id="description"
                          name="description"
                          value={mealForm.description}
                          onChange={handleInputChange}
                          className={formErrors.description ? 'error' : ''}
                          placeholder="Enter meal description..."
                          rows="4"
                        />
                        {formErrors.description && (
                          <span className="error-text">{formErrors.description}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => navigate('/school/dashboard')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Adding Meal...' : 'Add Meal'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowSuccessPopup(false);
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)'
            }}>
              <span style={{ fontSize: '3rem', color: '#FFFFFF' }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              textAlign: 'center',
              color: '#1F2937',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '0.5rem'
            }}>
              Meal Added Successfully!
            </h2>

            {/* Subtitle */}
            <p style={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.95rem',
              marginBottom: '2rem'
            }}>
              Your meal has been added to the system
            </p>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowSuccessPopup(false);
              }}
              style={{
                width: '100%',
                padding: '0.875rem 1.5rem',
                background: '#FF8C42',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#FFA366';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(255, 140, 66, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#FF8C42';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddMeal;

